import { EResourceStateKey, EResourceType, IAnimClipPlayData } from '@nova/types';
import { Logger } from '@nova/utils';
import {
  AnimatedSprite,
  Assets,
  Sprite,
  Spritesheet,
  Ticker
} from 'pixi.js';

import { AssetUtils } from '../assets/AssetUtils';
import { SpriteSheetMetaData } from '../assets/loaders/ExtendedSpriteSheetLoader';
import { AudioManager } from '../audio/AudioManager';
import { IScene } from '../scene/IScene';

export interface ISpriteAnimInitParams {
  x?: number;
  y?: number;
  assetKey?: string;
  addToScene?: boolean;
  hide?: boolean;
  hideOnComplete?: boolean;
  clipName?: string;
  timeScale?: number;
  animCompleteCallback?: () => void;
}

export class AnimUtils {
  public static spriteVfxPool: { [key: string]: AnimatedSprite[] } = {};
  public static readonly BASE_ANIMATION_SCALER: number = 0.4; // ! - This is being set to compensate for Phasers default of 24fps

  public static cancelAnimAndHide(sprite: AnimatedSprite): void {
    if (!sprite) { return; }

    sprite.gotoAndStop(0);
    sprite.visible = false;
  }

  public static resetAnimatedSprite(sprite: AnimatedSprite) {
    sprite.visible = true;
    sprite.stop();
    sprite.animationSpeed = 1;
  }

  public static getPoolKey(assetKey: string, clipName: string): string {
    return `${assetKey}_${clipName}`;
  }

  /**
   * Clears all sprite pools.
   */
  public static clearSpriteVfxPool(): void {
    for (const pool of Object.values(this.spriteVfxPool)) {
      for (const sprite of pool) {
        if (sprite.parent) {
          sprite.parent.removeChild(sprite);
        }
      }
    }
    this.spriteVfxPool = {};
  }

  /**
   * Gets a pooled sprite from the pool, or creates a new one if none are available.
   *
   * @param poolKey - Unique key identifying the sprite pool (typically assetKey_clipName)
   * @param createFn - Function to create a new sprite if pool is empty
   * @returns An AnimatedSprite from the pool or newly created
   */
  public static getPooledAnimatedSprite(poolKey: string, createFn: () => AnimatedSprite): AnimatedSprite {
    const pool = this.spriteVfxPool[poolKey] || [];
    // A sprite is available if it's not playing an animation
    const availableSprite = pool.find((sprite) => !sprite.playing);

    if (availableSprite) {
      // AnimUtils.resetAnimatedSprite(availableSprite);
      return availableSprite;
    }

    const newSprite = createFn();

    if (!this.spriteVfxPool[poolKey]) {
      this.spriteVfxPool[poolKey] = [];
    }
    this.spriteVfxPool[poolKey].push(newSprite);
    return newSprite;
  }

  /**
   * Returns a sprite to the pool for reuse
   *
   * @param poolKey - Unique key identifying the sprite pool
   * @param sprite - The sprite to return to the pool
   */
  public static returnSpriteToPool(poolKey: string, sprite?: AnimatedSprite): void {
    if (!sprite) { return; }

    // Cancel animation and hide the sprite
    sprite.visible = false;
    sprite.stop();
    sprite.currentFrame = 0;

    if (sprite.parent) {
      sprite.parent.removeChild(sprite);
    }

    // Ensure sprite is in the pool
    const pool = this.spriteVfxPool[poolKey] ||= [];
    if (!pool.includes(sprite)) {
      pool.push(sprite);
    }
  }

  public static setAnimationDurationForAnimatedSprite(sprite: AnimatedSprite, duration: number): void {
    const totalDurationInSeconds = sprite.totalFrames / sprite.animationSpeed / Ticker.shared.FPS;
    const newTimeScale = totalDurationInSeconds / (duration / 1000);
    sprite.animationSpeed = AnimUtils.BASE_ANIMATION_SCALER * newTimeScale;
  }

  public static playSpriteAnimationWithData(animation: AnimatedSprite, clipData: IAnimClipPlayData, timeScalar = 1) {
    this.resetAnimatedSprite(animation);
    if (!!clipData.timeToMatch && clipData.timeToMatch > 0) {
      const timeToMatch = clipData.timeToMatch * timeScalar;
      const totalDurationInSeconds = animation.totalFrames / animation.animationSpeed / Ticker.shared.FPS;
      const newTimeScale = totalDurationInSeconds / (timeToMatch / 1000);
      animation.animationSpeed = AnimUtils.BASE_ANIMATION_SCALER * newTimeScale;
    }
    else {
      animation.animationSpeed = AnimUtils.BASE_ANIMATION_SCALER;
    }

    animation.loop = (clipData.repeat === -1);
    animation.play();
  }

  public static createSpriteAnimationForPool(
    scene: IScene,
    addToScene: boolean,
    x: number,
    y: number,
    assetKey: string,
    clipName: string,
    depth?: number,
    timeScale = 1,
    scaleTo?: number): Sprite {
    const poolKey = this.getPoolKey(assetKey, clipName);
    const sprite = this.getPooledAnimatedSprite(poolKey, () => {
      const spriteSheet = Assets.get<Spritesheet<SpriteSheetMetaData>>(assetKey);
      const newSprite = AssetUtils.createAnimationFromSpriteSheet(spriteSheet, clipName);
      newSprite.onComplete = () => {
        AnimUtils.returnSpriteToPool(poolKey, newSprite);
      };
      return newSprite;
    });

    sprite.position.set(x, y);
    if (addToScene) {
      if (!scene.children.includes(sprite)) {
        scene.addChild(sprite);
      }
    }

    if (scaleTo !== undefined) {
      sprite.scale.set(scaleTo);
    }

    if (depth !== undefined) {
      sprite.zIndex = depth;
    }

    sprite.animationSpeed = timeScale;
    sprite.play();

    return sprite;
  }

  public static createChainAnimation(spriteSheet: Spritesheet, clips: string[], timeScalar = 1): AnimatedSprite[] {
    const animations: AnimatedSprite[] = [];

    for (const clip of clips) {
      const animationData = (spriteSheet.data as SpriteSheetMetaData).meta.clips[clip];
      const frames = AssetUtils.generateFramesForAnimation(spriteSheet, animationData);
      const animation = new AnimatedSprite(frames);
      animation.loop = false;
      animation.animationSpeed = timeScalar;
      animations.push(animation);
    }
    return animations;
  }

  public static playChainAnimation(clips: AnimatedSprite[]): void {
    // Reset any playing animations
    for (const clip of clips) {
      AnimUtils.cancelAnimAndHide(clip);
    }

    let playAnimClipIndex = 0;
    const chainFunc = () => {
      if (playAnimClipIndex === clips.length - 1) {
        const finalClip = clips[playAnimClipIndex];
        AnimUtils.cancelAnimAndHide(finalClip);
        finalClip.onComplete = undefined;
        return;
      }

      const previousClip = clips[playAnimClipIndex];
      AnimUtils.cancelAnimAndHide(previousClip);
      previousClip.onComplete = undefined;

      playAnimClipIndex++;

      const nextClip = clips[playAnimClipIndex];
      nextClip.visible = true;
      nextClip.onComplete = chainFunc;
      nextClip.play();
    };

    const firstClip = clips[0];
    firstClip.visible = true;
    firstClip.onComplete = chainFunc;
    firstClip.play();
  }

  /**
   * Uses the specified resources for the given object.
   *
   * @param resourceSet - The set of resources to use.
   * @param object - The object to apply the resources to.
   * @returns True if the resources were successfully applied, false otherwise.
   */
  public static useResource(resourceSet: { [resourceType: string]: unknown }, object?: AnimatedSprite): boolean {
    const clipData = resourceSet[EResourceStateKey.animationClip] as IAnimClipPlayData;
    const trackKey = resourceSet[EResourceType.audioTrack] as string;

    if (object instanceof AnimatedSprite && clipData) {
      AnimUtils.playSpriteAnimationWithData(object, clipData);
    }

    if (trackKey) {
      AudioManager.SFX.play(trackKey).catch((err: unknown) => {
        Logger.error(`Failed to play audio track: ${trackKey}`, err);
      });
      return true;
    }

    return false;
  }
}
